import clsx from 'clsx';
import React, { useCallback, useMemo, useRef } from 'react';
import './index.scss';

import { URLS } from '@/src/api/inbox';
import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import WysiwygEditor from '@/src/components/common/WysiwygEditor';
import { useToggle } from '@/src/hooks/useToggle';
import { getTextFromHtml } from '@/src/util/html';
import { ACCEPT_ATTRIBUTE } from '@/src/config/fileTypes';

import Uploader from './Uploader/Uploader';

import { uploadInlineImage } from '../../api/uploadInlineImage';
import { INLINE_IMAGE_DATA_ATTRIBUTE, UPLOAD_SOURCES } from '../../Config/inbox.config';
import { parseInlineImages } from '../../Helper';

const EMAIL_CONTENT_ID = 'ccfo-email-content';
const EMAIL_SIGNATURE_ID = 'ccfo-email-signature';
const PREVIOUS_MESSAGE_CONTAINER_ID = 'ccfo-previous-message';
const PREVIOUS_MESSAGE_CONTENT_ID = 'ccfo-previous-message-content';
const PREVIOUS_MESSAGE_TOGGLER_ID = 'ccfo-message-history-toggle';

type FormattedDocument = {
  filename: string;
  origin: string;
  size: number;
  id?: number;
  cid?: string;
};

const formatDocument = (document, isInlineImage = false) => {
  const formattedDocument: FormattedDocument = {
    filename: document.filename,
    origin: document.origin,
    size: document.size,
  };

  if (document.source === UPLOAD_SOURCES.LIBRARY) {
    formattedDocument.id = document.id;
  }

  if (isInlineImage) {
    formattedDocument.cid = document.id;
  }

  return formattedDocument;
};

const EmailEditor = ({
  files,
  setHasInput,
  onSend,
  onUpload,
  onUploading,
  submitEnabled,
  emailSignature,
  uploaderId,
  previousMessage = null,
  editorInitConfig = {},
}) => {
  const editorRef = useRef(null);
  const [showAttachmentLibraryModal, toggleShowAttachmentLibraryModal] = useToggle(false);
  const [showUploadLocal, toggleShowUploadLocal] = useToggle(false);
  const currentInlineImages = useRef([]);
  const inlineImagesTempUrl = useRef({});
  const inlineImagesFilenameFromBlob = useRef({});

  const parsedMessageHistory = useMemo(
    () => (previousMessage ? parseInlineImages(previousMessage.html) : null),
    [previousMessage]
  );

  const editorInitialValue = useMemo(() => {
    return `<div class="editable-content" id="${EMAIL_CONTENT_ID}">
      ${
        emailSignature || previousMessage
          ? `<p style="margin: 0px;" data-mce-style="margin: 0px;"></p>
             <p style="margin: 0px;" data-mce-style="margin: 0px;"></p>`
          : ''
      }
    </div>
      <div class="noneditable-content">
       ${
         emailSignature
           ? `<div class="editable-content" id="${EMAIL_SIGNATURE_ID}">${emailSignature}</div>`
           : ''
       }
       ${
         previousMessage
           ? `<div id="${PREVIOUS_MESSAGE_CONTAINER_ID}" style="margin-top: 10px; padding-top: 10px;">
                <button id="${PREVIOUS_MESSAGE_TOGGLER_ID}">
                  <i class="material-icons">more_horiz</i>
                </button>
                <p style="margin: 0px;" data-mce-style="margin: 0px;"></p>
                <div id="${PREVIOUS_MESSAGE_CONTENT_ID}" style="display: none;">${parsedMessageHistory}</div>
              </div>`
           : ''
       }
       </div>
     `;
  }, [emailSignature, parsedMessageHistory, previousMessage]);

  const editorSetup = useMemo(
    () => (editor) => {
      editor.ui.registry.addIcon('materialAttachFile', '<i class="material-icons">attach_file</i>');

      editor.ui.registry.addMenuButton('attachment', {
        icon: 'materialAttachFile',
        tooltip: 'Attachment',
        fetch: (callback) => {
          callback([
            {
              type: 'menuitem',
              text: 'Upload From Local',
              onAction: () => {
                toggleShowUploadLocal(true);
              },
            },
            // {
            //   type: 'menuitem',
            //   text: 'Upload From Library',
            //   onAction: () => {
            //     toggleShowAttachmentLibraryModal(true);
            //   },
            // },
          ]);
        },
      });

      editor.on('click', (event) => {
        const eventTarget = event.target as HTMLElement;

        if (eventTarget.closest(`button#${PREVIOUS_MESSAGE_TOGGLER_ID}`)) {
          const previousMessageElement = editor.dom.get(PREVIOUS_MESSAGE_CONTENT_ID);
          if (previousMessageElement) {
            const currentDisplay = editor.dom.getStyle(previousMessageElement, 'display');
            editor.dom.setStyle(
              previousMessageElement,
              'display',
              currentDisplay === 'none' ? 'block' : 'none'
            );
          }
        }
      });

      // @TODO: check this block again on implementation of pasting images
      // Listen to the paste event
      // editor.on('paste', function (e) {
      //   const clipboardData = e.clipboardData || window.clipboardData;
      //   const pastedData = clipboardData.getData('Text');

      //   // Check if the pasted content is an image URL
      //   if (pastedData.match(/\.(jpeg|jpg|gif|png)$/) !== null) {
      //     // Prevent the default paste behavior
      //     e.preventDefault();

      //     // Insert the image element at the current cursor position
      //     editor.insertContent(`<img src="${pastedData}" alt="Pasted Image" />`);
      //   }
      // });
    },
    [toggleShowAttachmentLibraryModal, toggleShowUploadLocal]
  );

  const handleUpload = (documentList) => {
    onUpload(documentList.map((document) => formatDocument(document)));
  };

  const handleUploadedInlineImages = (document) => {
    currentInlineImages.current = [...currentInlineImages.current, formatDocument(document, true)];
  };

  const handleEditorChange = (newValue, editor) => {
    const emailContent = editor.dom.get(EMAIL_CONTENT_ID).innerHTML;
    const isEditorEmpty = !!getTextFromHtml(emailContent).trim();

    setHasInput(isEditorEmpty);
  };

  const handleSubmit = useCallback(async () => {
    editorRef.current
      .uploadImages()
      .then(() => {
        const editorContent = editorRef.current.dom.get(EMAIL_CONTENT_ID).innerHTML;
        const emailSignatureContent =
          editorRef.current.dom.get(EMAIL_SIGNATURE_ID)?.innerHTML ?? '';
        let sanitizedEditorContent = editorContent.replace(/(cid:[^"]+)\?\d+/g, '$1');
        sanitizedEditorContent = sanitizedEditorContent.replace(/\ssrc="blob:[^"]+"/g, (match) => {
          const tempUrl = match.slice(6, -1);
          const cid = inlineImagesTempUrl.current[tempUrl];
          const inlineImageDataIndex = currentInlineImages.current.findIndex(
            (inlineImage) => `cid:${inlineImage.cid}` === cid
          );

          URL.revokeObjectURL(tempUrl); // Remove object url from cache
          return ` src="${cid}" ${INLINE_IMAGE_DATA_ATTRIBUTE}="${currentInlineImages.current[inlineImageDataIndex].filename}"`;
        });

        const messageHTML = `${sanitizedEditorContent}${emailSignatureContent}`;
        const rawMessage = getTextFromHtml(messageHTML);

        onSend({ messageHTML, rawMessage, currentInlineImages: currentInlineImages.current });
      })
      .catch((error) => {
        console.log(error.message);
      });
  }, [currentInlineImages, onSend]);

  const filePickerCallback = (cb) => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.addEventListener('change', (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files[0];

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        /*
          Note: Now we need to register the blob in TinyMCEs image blob
          registry. In the next release this part hopefully won't be
          necessary, as we are looking to handle it internally.
        */
        const id = 'blobid' + new Date().getTime();
        const blobCache = editorRef.current.editorUpload.blobCache;
        const base64 = (reader.result as string).split(',')[1];
        const blobInfo = blobCache.create(id, file, base64);
        blobCache.add(blobInfo);
        inlineImagesFilenameFromBlob.current = {
          ...inlineImagesFilenameFromBlob.current,
          [blobInfo.filename()]: file.name,
        };

        /* call the callback and populate the Title field with the file name */
        cb(blobInfo.blobUri(), { title: file.name });
      });
      reader.readAsDataURL(file);
    });

    input.click();
  };

  const handleImagesUpload = async (blobInfo): Promise<string> => {
    let responseError = null;
    let imagePath = null;
    let uploadedImage = null;
    let tempUrl = '';

    const formData = new FormData();
    formData.append(
      'file',
      blobInfo.blob(),
      inlineImagesFilenameFromBlob.current[blobInfo.filename()]
    );

    try {
      const response = await uploadInlineImage(formData);
      imagePath = `cid:${response.data.results[0].id}`;
      uploadedImage = response.data.results[0];
      tempUrl = URL.createObjectURL(blobInfo.blob());

      inlineImagesTempUrl.current = {
        ...inlineImagesTempUrl.current,
        [tempUrl]: imagePath,
      };
    } catch (error) {
      responseError = error;
    }

    return new Promise((resolve, reject) => {
      if (responseError) reject({ message: 'Error uploading image', remove: true });

      handleUploadedInlineImages(uploadedImage);
      resolve(tempUrl);
    });
  };

  return (
    <>
      <Uploader
        accept={ACCEPT_ATTRIBUTE}
        url={URLS.UPLOAD_DOCUMENT}
        maxFiles={1000}
        sourceFiles={files}
        auto
        hide
        onUpload={handleUpload}
        onUploading={onUploading}
        id={uploaderId}
        section={'email-editor'}
        onDelete={null}
        showLibraryModal={showAttachmentLibraryModal}
        toggleShowLibraryModal={toggleShowAttachmentLibraryModal}
        showUploadLocal={showUploadLocal}
        toggleShowUploadLocal={toggleShowUploadLocal}
      />
      <WysiwygEditor
        editorRef={editorRef}
        mainEditorConfig={{
          initialValue: editorInitialValue,
          onEditorChange: handleEditorChange,
        }}
        editorInitConfig={{
          height: '100%',
          menubar: false,
          statusbar: false,
          plugins: 'lists link image table emoticons help',
          toolbar:
            'bold italic underline strikethrough forecolor | numlist bullist align | emoticons image link attachment | table',
          // default_font_stack: ['Arial'],
          font_size_formats: '8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt',
          toolbar_mode: 'sliding',
          toolbar_location: 'bottom',
          editable_root: false,
          content_css: '/stylesheets/material-icons.css',
          content_style: `
            body { 
              font-size: 14px; 
            }

            #${PREVIOUS_MESSAGE_TOGGLER_ID} {
            color: #5f6368;
            font-size: 14px;
            padding: 3px 10px;
            background-color: white;
            border: 1px solid #eeeeee;
            border-radius: 34px;
            display: flex;
            align-items: center;
            cursor: pointer;
          }
          `,
          forced_root_block_attrs: {
            style: 'margin: 0px;',
          },
          setup: editorSetup,
          file_picker_callback: filePickerCallback,
          images_upload_handler: handleImagesUpload,
          // images_reuse_filename: true,
          file_picker_types: 'image',
          automatic_uploads: false,
          ...editorInitConfig,
        }}
      />
      <div
        id={`${uploaderId}-attachments-upload-status-container`}
        className={clsx(
          files.length > 0 && 'tw-border-t',
          'tw-relative tw-border-b tw-border-cfo-grey-500'
        )}
      ></div>
      <div className="tw-flex tw-justify-end tw-p-[10px]">
        <Button
          disabled={!submitEnabled}
          onClick={handleSubmit}
          buttonClassSettings={{
            variant: 'secondary',
            size: 'sm',
            activeStyles: 'tw-text-white tw-border-cfo-blue-700 tw-bg-cfo-blue-700',
          }}
          className="tw-py-[5px] tw-px-[10px] tw-mr-[10px] disabled:tw-pointer-events-none disabled:tw-bg-cfo-grey-100 disabled:tw-border-cfo-grey-500"
        >
          <Icon
            icon={ICONS.send}
            className="tw-mr-[5px] tw-text-[16px]"
            style={{ transform: 'rotate(-45deg) translateY(-3px)' }}
          />
          Send
        </Button>
      </div>
    </>
  );
};

export default EmailEditor;
